import React, { useMemo, useState } from "react";
import { Box, Divider, useTheme, useMediaQuery } from "@mui/material";
import { useTheme as useCustomTheme } from "../util/themes";
import { BreadcrumbSection } from "../components/Checkout/BreadcrumbSection";
import { ContactSection } from "../components/Checkout/ContactSection";
import { ContinueButton } from "../components/Checkout/ContinueButton";
import { CouponSection } from "../components/Checkout/CouponSection";
import { mainContainerStyle, leftColumnStyle } from "../components/Checkout/CouponStyles";
import { DeliverySection } from "../components/Checkout/DeliverySection";
import {
    CheckoutProcessStep,
    CheckoutFormTypeFields,
    useCheckoutForm,
} from "../components/Checkout/hooks/useCheckoutForm";
import { usePriceCalculator } from "../components/Checkout/hooks/usePriceCalculator";
import { ZipCodeField } from "../components/Checkout/ZipcodeField";
import { OrderDeliveryAddressFields } from "../types/purchaseOrder/Order";
import { MobileOrderSummary } from "../components/Checkout/MobileOrderSummary";
import { OrderSummarySection } from "../components/Checkout/OrderSummarySection";
import { VerticalDivider } from "../components/Checkout/VerticalDivider";
import { PersonalInformationSection } from "../components/Checkout/PersonalInformationSection";
import { DeliveryAddressInformationSection } from "../components/Checkout/DeliveryAddressInformationSection";
import { PaymentSection } from "../components/Checkout/PaymentSection";
import { CheckoutFinishedResult } from "../components/Checkout/CheckoutFinishedResult";
import { CheckoutStockConflictResolver } from "../components/Checkout/CheckoutStockConflictResolver";
import { CheckoutProcessStatusType } from "../components/Checkout/hooks/useCheckoutForm";
import { CheckoutBlocked } from "../components/Checkout/CheckoutBlocked";
import { PaymentOptionSection } from "../components/Checkout/PaymentOptionSection";
import { CamposAdicionalesSection } from "../components/Checkout/CamposAdicionalesSection";

const CheckoutPage: React.FC = () => {
    const {
        methods,
        onSubmitZip,
        onSubmitCustomerDetail,
        onSubmitOrder,
        confirmedZipCode,
        currentStep,
        isHomeDelivery,
        processStatus,
        cartItems,
        couponStatus,
        setCouponStatus,
        deliveryType,
        deliveryOptions,
        applyCoupon,
        isLoadingZipCode,
        isLoadingCustomerDetail,
        isLoadingOrder,
        recalculateOrderWithoutCoupon,
        isCheckoutBlocked,
        lstCamposAdicionales,
        camposAdicionalesValores,
        setCampoAdicionalValor,
        ...form
    } = useCheckoutForm();

    const submitContinueButtonProps = useMemo(() => {
        switch (currentStep) {
            case CheckoutProcessStep.ZipCode:
                return {
                    onClick: onSubmitZip,
                    label: "Continuar",
                    isLoading: isLoadingZipCode,
                };

            case CheckoutProcessStep.CustomerDetails:
                return {
                    onClick: onSubmitCustomerDetail,
                    label: "Continuar con el pedido",
                    isLoading: isLoadingCustomerDetail,
                };

            case CheckoutProcessStep.PaymentAndConfirmation:
                return {
                    onClick: onSubmitOrder,
                    label: "Realizar Pedido",
                    isLoading: isLoadingOrder,
                };

            default:
                return {
                    onClick: onSubmitZip,
                    label: "Continuar",
                    isLoading: false,
                };
        }
    }, [currentStep, isLoadingZipCode, isLoadingCustomerDetail, isLoadingOrder]);
    const isCompleteProcess = useMemo(
        () => currentStep === CheckoutProcessStep.Finished,
        [currentStep],
    );

    const deliveryPrice = useMemo(() => {
        if (!deliveryType) return 0;

        const selected = deliveryOptions
            .flatMap((g: any) => g.options)
            .find((o: any) => `${o.value}` === `${deliveryType}`);

        if (!selected || selected.isToBeAgreed) return 0;

        return selected.priceNumber ?? 0;
    }, [deliveryType, deliveryOptions]);

    const isShippingToBeAgreed = useMemo(() => {
        if (!deliveryType) return false;

        const selected = deliveryOptions
            .flatMap((g: any) => g.options)
            .find((o: any) => `${o.value}` === `${deliveryType}`);

        return !!selected?.isToBeAgreed;
    }, [deliveryType, deliveryOptions]);

    const priceSummary = usePriceCalculator(
        cartItems,
        couponStatus.appliedDiscount,
        deliveryPrice,
        couponStatus.calculationData,
        isShippingToBeAgreed,
    );
    const muiTheme = useTheme();
    const customTheme = useCustomTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
    const [showOrderDetails, setShowOrderDetails] = useState(false);

    if (!isCompleteProcess && isCheckoutBlocked) {
        return <CheckoutBlocked />;
    }

    const handleCouponSubmit = (name: string) => applyCoupon(name);

    const couponSectionProps = {
        showCouponField: form.showCouponField,
        couponCode: form.couponCode,
        onShowCouponField: () => {
            form.setShowCouponField(true);
            setCouponStatus({
                loading: false,
                error: null,
                success: false,
                appliedDiscount: couponStatus.appliedDiscount,
                calculationData: couponStatus.calculationData,
            });
        },
        onCouponDelete: () => {
            recalculateOrderWithoutCoupon();
        },
        onCouponCodeChange: form.setCouponCode,
        onCouponSubmit: handleCouponSubmit,
        couponStatus: couponStatus,
    };

    const hasSelectedShippingOption = !!deliveryType;

    return (
        <>
            {!isCompleteProcess && (
                <React.Fragment>
                    <BreadcrumbSection
                        paymentActive={currentStep === CheckoutProcessStep.PaymentAndConfirmation}
                    />
                    <Divider sx={{ mt: 1, borderColor: "grey.300" }} />
                </React.Fragment>
            )}

            {isMobile && (
                <MobileOrderSummary
                    cartItems={cartItems}
                    priceSummary={priceSummary}
                    hasSelectedShippingOption={hasSelectedShippingOption}
                    onToggle={setShowOrderDetails}
                />
            )}

            <Box
                sx={{
                    backgroundColor:
                        isMobile && showOrderDetails
                            ? customTheme.colors.background.paper
                            : "transparent",
                    transition: "background-color 0.3s ease",
                }}
            >
                <Box sx={mainContainerStyle}>
                    <VerticalDivider />

                    <Box sx={leftColumnStyle}>
                        {processStatus.type ===
                        CheckoutProcessStatusType.RequiresStockResolution ? (
                            <CheckoutStockConflictResolver />
                        ) : isCompleteProcess ? (
                            <CheckoutFinishedResult />
                        ) : currentStep === CheckoutProcessStep.PaymentAndConfirmation ? (
                            <PaymentSection />
                        ) : (
                            <React.Fragment>
                                <ContactSection />

                                {currentStep === CheckoutProcessStep.ZipCode ? (
                                    <Box sx={{ mb: 4 }}>
                                        <ZipCodeField
                                            control={methods.control}
                                            name={
                                                `${CheckoutFormTypeFields.DeliveryAddress}.${OrderDeliveryAddressFields.ZipCode}` as any
                                            }
                                        />
                                    </Box>
                                ) : (
                                    <>
                                        {isMobile && <CouponSection {...couponSectionProps} />}

                                        <PersonalInformationSection />

                                        {confirmedZipCode && isHomeDelivery && (
                                            <DeliveryAddressInformationSection />
                                        )}

                                        <DeliverySection />

                                        <PaymentOptionSection />

                                        <Box sx={{ mb: 3 }}>
                                            <CamposAdicionalesSection
                                                lstCampos={lstCamposAdicionales}
                                                valores={camposAdicionalesValores}
                                                onChange={setCampoAdicionalValor}
                                            />
                                        </Box>
                                    </>
                                )}
                            </React.Fragment>
                        )}

                        {processStatus.type !== CheckoutProcessStatusType.RequiresStockResolution &&
                            currentStep !== CheckoutProcessStep.Finished && (
                                <ContinueButton
                                    onClick={submitContinueButtonProps.onClick}
                                    isLoading={submitContinueButtonProps.isLoading}
                                >
                                    {submitContinueButtonProps.label}
                                </ContinueButton>
                            )}
                    </Box>

                    {!isMobile && (
                        <OrderSummarySection
                            cartItems={cartItems}
                            priceSummary={priceSummary}
                            hasSelectedShippingOption={hasSelectedShippingOption}
                            couponSectionProps={!isCompleteProcess ? couponSectionProps : undefined}
                        />
                    )}
                </Box>
            </Box>
        </>
    );
};

export default CheckoutPage;
